export function encodeParams(params) {
	params = { params: JSON.stringify(params) };
	const encodedParams = Object.keys(params)
		.map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
		.join('&');
	return encodedParams;
}

export function decodeParams(encodedParams) {
	let decodedParams: any = {};
	// Check if encodedParams is not null or undefined
	if (encodedParams) {
		const queryParams = new URLSearchParams(encodedParams);
		// Iterate over each query parameter
		queryParams.forEach((value, key) => {
			// Decode the value and assign it to the decodedParams object
			decodedParams[key] = decodeURIComponent(value);
		});
		// Check if decodedParams has a 'params' key
		if ('params' in decodedParams) {
			// Return the value of 'params'
			decodedParams = JSON.parse(decodedParams.params);
		}
	}
	// Return the entire decodedParams object if 'params' key is not found or encodedParams is null/undefined
	return decodedParams;
}

/**
 * Builds a UTM query string. `utm_source` defaults to "app"; `utm_campaign`
 * is the dynamic identifier for the entry point (e.g. the poll card name).
 * @example buildUtmParams({ campaign: 'community_revamp_poll_card' })
 *   // → "utm_source=app&utm_campaign=community_revamp_poll_card"
 */
export function buildUtmParams({
	source = 'app',
	campaign,
}: {
	source?: string;
	campaign: string;
}): string {
	const params = new URLSearchParams();
	params.set('utm_source', source);
	params.set('utm_campaign', campaign);
	return params.toString();
}

/**
 * Appends UTM params to a path/URL, preserving any existing query string.
 * @example withUtm('/leaderboard', { campaign: 'community_revamp_poll_card' })
 *   // → "/leaderboard?utm_source=app&utm_campaign=community_revamp_poll_card"
 */
export function withUtm(
	path: string,
	utm: { source?: string; campaign: string },
): string {
	const query = buildUtmParams(utm);
	return path.includes('?') ? `${path}&${query}` : `${path}?${query}`;
}

/**
 * Extracts UTM parameters from a URL string or URL object
 * @param url - URL string or URLSearchParams string (e.g., "?utm_source=google&utm_medium=cpc")
 * @returns Object containing all UTM parameters found (every key that matches `/^utm_[a-z0-9_]+$/i`)
 */
export function extractUtmParams(
	url: string | URLSearchParams,
): Record<string, string> {
	const utmParams: Record<string, string> = {};

	let searchParams: URLSearchParams;

	if (url instanceof URLSearchParams) {
		searchParams = url;
	} else if (typeof url === 'string') {
		// Handle both full URLs and query strings
		if (url.startsWith('http://') || url.startsWith('https://')) {
			try {
				const urlObj = new URL(url);
				searchParams = urlObj.searchParams;
			} catch (e) {
				// If URL parsing fails, try treating it as a query string
				searchParams = new URLSearchParams(url);
			}
		} else {
			// Treat as query string
			searchParams = new URLSearchParams(url);
		}
	} else {
		return utmParams;
	}

	// Regex-based extraction so any custom utm keys also get forwarded.
	// Example supported keys: utm_source, utm_medium, utm_campaign, utm_social, utm_content, etc.
	const UTM_KEY_PATTERN = /^utm_[a-z0-9_]+$/i;
	for (const [key, value] of searchParams.entries()) {
		if (!UTM_KEY_PATTERN.test(key)) continue;
		if (value == null || value === '') continue;
		utmParams[key] = decodeURIComponent(value);
	}

	return utmParams;
}
